import { createEvent, createStore, sample, combine } from 'effector';
import type { Sight, SightId, CameraModeConfig } from '../types';
import { sceneLoadingReset } from './sceneLoading';

export type ApplicationState = {
  sceneId: string | number | null;
  sights: Record<SightId, Sight>;
  orderedSightIds: SightId[];
  state:
    | {
        type: 'root';
      }
    | {
        type: 'free-camera';
      }
    | {
        type: 'sight-details';
        sightId: SightId | null;
      };
};

export interface NavigationOptions {
  smooth?: boolean;
  currentTime?: number;
}

export type SightSelectionSource = 'poi' | 'assistant';

export type SightSelectionPayload = {
  id: SightId;
  source?: SightSelectionSource;
} & NavigationOptions;

const initialState: ApplicationState = {
  sceneId: null,
  sights: {},
  orderedSightIds: [],
  state: {
    type: 'free-camera',
  },
};

export const setSceneId = createEvent<string | number | null>();
export const setSights = createEvent<Record<SightId, Sight>>();
export const setOrderedSightIds = createEvent<SightId[]>();

export const goToSight = createEvent<{ id: SightId } & NavigationOptions>();
export const userSelectedSight = createEvent<SightSelectionPayload>();
export const clearFocusedSight = createEvent();
export const userInteracted = createEvent();
/**
 * Fires when the user STARTS manipulating the 3D scene camera (orbit-drag /
 * pinch-zoom) — dispatched from `controls.onOrbitInteractionStart`, which is
 * wired to Three.js OrbitControls' user-input "start" event. Distinct from
 * `userInteracted` (which also fires on chat submit) and never fires on POI
 * taps or programmatic camera moves. Consumed by `AssistantHud` to auto-expand
 * the 3D view (collapse the half-screen chat) on scene interaction.
 */
export const sceneOrbitInteractionStarted = createEvent();
export const updateSightCameraConfig = createEvent<{
  sightId: SightId;
  cameraConfig: Partial<CameraModeConfig>;
}>();

export const $appState = createStore<ApplicationState>(initialState)
  .on(setSceneId, (state, sceneId) => ({ ...state, sceneId }))
  .on(setSights, (state, sights) => ({ ...state, sights }))
  .on(setOrderedSightIds, (state, orderedSightIds) => ({
    ...state,
    orderedSightIds,
  }))
  .on(updateSightCameraConfig, (state, { sightId, cameraConfig }) => {
    const sight = state.sights[sightId];
    if (!sight) return state;
    return {
      ...state,
      sights: {
        ...state.sights,
        [sightId]: {
          ...sight,
          cameraConfig: { ...sight.cameraConfig, ...cameraConfig },
        },
      },
    };
  });

export const $sightsStore = $appState.map((state) => state.sights);
export const $orderedSightIds = $appState.map((state) => state.orderedSightIds);
export const $sceneId = $appState.map((state) => state.sceneId);

export const $sceneSights = combine(
  $sightsStore,
  $sceneId,
  (sights, sceneId) => {
    if (sceneId === null) return sights;
    const filtered: Record<SightId, Sight> = {};
    for (const [id, sight] of Object.entries(sights)) {
      if (sight.sceneId === sceneId) {
        filtered[id as SightId] = sight;
      }
    }
    return filtered;
  }
);

export const $sceneOrderedSightIds = combine(
  $orderedSightIds,
  $sightsStore,
  $sceneId,
  (ids, sights, sceneId) => {
    if (sceneId === null) return ids;
    return ids.filter((id) => sights[id]?.sceneId === sceneId);
  }
);
/**
 * Sights withheld from the POI surfaces for as long as their subject is not
 * where the marker says it is. The only caller today is the opening crow tour:
 * skipping it hands the camera back while the bird is still in the air, and a
 * marker on an empty perch is a lie the user can click.
 *
 * Deliberately NOT a filter on `$sceneSights`: `SceneCanvas`'s effect has
 * `[sights, config]` deps and re-creates the whole scene when the array changes,
 * so hiding a marker that way would restart the preloader. The 3D layer is told
 * directly (`crowBirdPoi.ts` takes the bird's POI out of the engine); this store
 * is what the chip strip under the chat input reads.
 */
export const suppressSight = createEvent<SightId>();
export const revealSuppressedSight = createEvent<SightId>();

export const $suppressedSightIds = createStore<ReadonlySet<SightId>>(new Set())
  .on(suppressSight, (state, id) => {
    if (state.has(id)) return state;
    const next = new Set(state);
    next.add(id);
    return next;
  })
  .on(revealSuppressedSight, (state, id) => {
    if (!state.has(id)) return state;
    const next = new Set(state);
    next.delete(id);
    return next;
  })
  // A scene change takes the bird with it; nothing may stay withheld across one.
  .reset(sceneLoadingReset);

const $focusedSightId = createStore<SightId | null>(null)
  .reset(setSights)
  .reset(sceneLoadingReset)
  .on(goToSight, (_, payload) => payload.id)
  .on(clearFocusedSight, () => null);

export const $currentSight = combine($sightsStore, $focusedSightId, (sights, sightId) =>
  sightId ? sights[sightId] ?? null : null
);

sample({
  clock: userSelectedSight,
  target: goToSight,
});
